/**
 * 訂單管理 API 服務
 * 使用 RTK Query 封裝訂單相關的 API 調用
 * 
 * 注意：目前使用 mock 數據進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IOrder,
  IOrderListParams,
  IOrderStatistics,
  IBatchUpdateOrdersRequest,
  IBatchUpdateOrdersResponse,
  IOrderStatusUpdateRequest,
  IOrderCancelRequest,
  IOrderRefundRequest,
  IOrderNoteRequest,
  IOrderNoteUpdateRequest,
  IOrderMarkRequest,
  IOrderHistoryParams,
  IOrderNote,
  IOrderMark,
  IOrderStatusHistory,
  IOrderLog,
  ITicketCancelVerificationRequest,
  ITicketRefundShoppingCreditRequest,
  ITicketVoidRequest,
} from '@/types/order';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 訂單 API 服務
 */
export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Order', 'OrderStatistics', 'OrderHistory', 'OrderLog', 'OrderNote', 'OrderMark'],
  endpoints: (builder) => ({
    // 獲取訂單列表
    getOrders: builder.query<IApiResponse<IPaginatedResponse<IOrder>>, IOrderListParams>({
      query: (params) => ({
        url: ConstantUtil.API_ORDERS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.orderNumber && { orderNumber: params.orderNumber }),
          ...(params.customerName && { customerName: params.customerName }),
          ...(params.customerPhone && { customerPhone: params.customerPhone }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.paymentStatus && params.paymentStatus !== 'all' && { paymentStatus: params.paymentStatus }),
          ...(params.startDate && { startDate: params.startDate }),
          ...(params.endDate && { endDate: params.endDate }),
          ...(params.minAmount !== undefined && { minAmount: params.minAmount }),
          ...(params.maxAmount !== undefined && { maxAmount: params.maxAmount }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Order'],
    }),

    // 獲取訂單詳情
    getOrderById: builder.query<IApiResponse<IOrder>, string>({
      query: (id) => ({
        url: ConstantUtil.API_ORDER_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),

    // 獲取訂單統計數據
    getOrderStatistics: builder.query<IApiResponse<IOrderStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_ORDER_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['OrderStatistics'],
    }),

    // 批量更新訂單
    batchUpdateOrders: builder.mutation<IApiResponse<IBatchUpdateOrdersResponse>, IBatchUpdateOrdersRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Order', 'OrderStatistics'],
    }),

    // 更新訂單狀態
    updateOrderStatus: builder.mutation<IApiResponse<IOrder>, IOrderStatusUpdateRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_UPDATE_STATUS(data.order_id),
        method: 'PUT',
        body: {
          new_status: data.new_status,
          reason: data.reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'OrderStatistics',
        'OrderHistory',
        'OrderLog',
      ],
    }),

    // 取消訂單
    cancelOrder: builder.mutation<IApiResponse<IOrder>, IOrderCancelRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_CANCEL(data.order_id),
        method: 'POST',
        body: {
          reason: data.reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'OrderStatistics',
        'OrderHistory',
        'OrderLog',
      ],
    }),

    // 退款訂單
    refundOrder: builder.mutation<IApiResponse<IOrder>, IOrderRefundRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_REFUND(data.order_id),
        method: 'POST',
        body: {
          amount: data.amount,
          refund_method: data.refund_method,
          reason: data.reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        'OrderStatistics',
        'OrderHistory',
        'OrderLog',
      ],
    }),

    // 獲取訂單歷史記錄
    getOrderHistory: builder.query<IApiResponse<IPaginatedResponse<IOrderStatusHistory>>, IOrderHistoryParams>({
      query: (params) => ({
        url: ConstantUtil.API_ORDER_HISTORY(params.order_id),
        method: 'GET',
        params: {
          type: params.type || 'all',
          startDate: params.startDate,
          endDate: params.endDate,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
        },
      }),
      providesTags: (_result, _error, { order_id }) => [{ type: 'OrderHistory', id: order_id }],
    }),

    // 獲取訂單操作日誌
    getOrderLogs: builder.query<IApiResponse<IPaginatedResponse<IOrderLog>>, IOrderHistoryParams>({
      query: (params) => ({
        url: ConstantUtil.API_ORDER_LOGS(params.order_id),
        method: 'GET',
        params: {
          type: params.type || 'all',
          startDate: params.startDate,
          endDate: params.endDate,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
        },
      }),
      providesTags: (_result, _error, { order_id }) => [{ type: 'OrderLog', id: order_id }],
    }),

    // 獲取訂單備註列表
    getOrderNotes: builder.query<IApiResponse<IOrderNote[]>, string>({
      query: (orderId) => ({
        url: ConstantUtil.API_ORDER_NOTES(orderId),
        method: 'GET',
      }),
      providesTags: (_result, _error, orderId) => [{ type: 'OrderNote', id: orderId }],
    }),

    // 添加訂單備註
    addOrderNote: builder.mutation<IApiResponse<IOrderNote>, IOrderNoteRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_NOTES(data.order_id),
        method: 'POST',
        body: {
          content: data.content,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'OrderNote', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 更新訂單備註
    updateOrderNote: builder.mutation<IApiResponse<IOrderNote>, IOrderNoteUpdateRequest & { order_id: string }>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_NOTE_BY_ID(data.order_id, data.note_id),
        method: 'PUT',
        body: {
          content: data.content,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'OrderNote', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 刪除訂單備註
    deleteOrderNote: builder.mutation<IApiResponse<void>, { order_id: string; note_id: string }>({
      query: ({ order_id, note_id }) => ({
        url: ConstantUtil.API_ORDER_NOTE_BY_ID(order_id, note_id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'OrderNote', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 獲取訂單標記列表
    getOrderMarks: builder.query<IApiResponse<IOrderMark[]>, string>({
      query: (orderId) => ({
        url: ConstantUtil.API_ORDER_MARKS(orderId),
        method: 'GET',
      }),
      providesTags: (_result, _error, orderId) => [{ type: 'OrderMark', id: orderId }],
    }),

    // 添加訂單標記
    addOrderMark: builder.mutation<IApiResponse<IOrderMark>, IOrderMarkRequest>({
      query: (data) => ({
        url: ConstantUtil.API_ORDER_MARKS(data.order_id),
        method: 'POST',
        body: {
          mark_type: data.mark_type,
          mark_label: data.mark_label,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'OrderMark', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 刪除訂單標記
    deleteOrderMark: builder.mutation<IApiResponse<void>, { order_id: string; mark_id: string }>({
      query: ({ order_id, mark_id }) => ({
        url: ConstantUtil.API_ORDER_MARK_BY_ID(order_id, mark_id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'OrderMark', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 取消票券核銷
    cancelTicketVerification: builder.mutation<IApiResponse<IOrder>, ITicketCancelVerificationRequest>({
      query: (data) => ({
        url: ConstantUtil.API_TICKET_CANCEL_VERIFICATION(data.order_id, data.item_id, data.ticket_id),
        method: 'POST',
        body: {
          reason: data.reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 票券退購物金
    refundTicketShoppingCredit: builder.mutation<IApiResponse<IOrder>, ITicketRefundShoppingCreditRequest>({
      query: (data) => ({
        url: ConstantUtil.API_TICKET_REFUND_SHOPPING_CREDIT(data.order_id, data.item_id, data.ticket_id),
        method: 'POST',
        body: {
          amount: data.amount,
          reason: data.reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),

    // 票券作廢
    voidTicket: builder.mutation<IApiResponse<IOrder>, ITicketVoidRequest>({
      query: (data) => ({
        url: ConstantUtil.API_TICKET_VOID(data.order_id, data.item_id, data.ticket_id),
        method: 'POST',
        body: {
          void_reason: data.void_reason,
        },
      }),
      invalidatesTags: (_result, _error, { order_id }) => [
        { type: 'Order', id: order_id },
        { type: 'OrderLog', id: order_id },
      ],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useGetOrderStatisticsQuery,
  useBatchUpdateOrdersMutation,
  useUpdateOrderStatusMutation,
  useCancelOrderMutation,
  useRefundOrderMutation,
  useGetOrderHistoryQuery,
  useGetOrderLogsQuery,
  useGetOrderNotesQuery,
  useAddOrderNoteMutation,
  useUpdateOrderNoteMutation,
  useDeleteOrderNoteMutation,
  useGetOrderMarksQuery,
  useAddOrderMarkMutation,
  useDeleteOrderMarkMutation,
  useCancelTicketVerificationMutation,
  useRefundTicketShoppingCreditMutation,
  useVoidTicketMutation,
} = orderApi;

